import type { PushDeviceBrowser } from '../hooks/api/pushNotifications';

/**
 * Piezas PURAS (sin tocar `navigator`) para describir el dispositivo en el registro de push: versión, navegador y modelo.
 * Todo sale ya derivado — al backend nunca viaja el user agent crudo. Las junta `collectDeviceInfo` (`pushDevice.ts`).
 */

/** "17_4" / "17.4.1" → "17.4" / "17.4.1" (como mucho 3 tramos de hasta 3 dígitos); `undefined` si no hay una versión legible. */
export function normalizeVersion(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const parts = raw.split(/[._]/).slice(0, 3);
  return parts.every((p) => /^\d{1,3}$/.test(p)) ? parts.join('.') : undefined;
}

/** Navegador según el user agent. Edge y Chrome en iOS/Android llevan su propio token, por eso van antes que Chrome/Safari. */
export function parseBrowser(userAgent: string): PushDeviceBrowser {
  if (/Edg(?:e|A|iOS)?\//i.test(userAgent)) return 'edge';
  if (/Firefox\/|FxiOS\//i.test(userAgent)) return 'firefox';
  if (/Chrome\/|CriOS\//i.test(userAgent)) return 'chrome';
  if (/Safari\//i.test(userAgent)) return 'safari';
  return 'other';
}

/**
 * Modelos de iPhone/iPad por tamaño de pantalla (px CSS, vertical) y densidad. Safari no expone el modelo, y varios
 * comparten pantalla: por eso cada entrada lista los candidatos y el resultado SIEMPRE se informa como aproximado.
 */
const APPLE_MODELS: ReadonlyArray<{ w: number; h: number; dpr: number; label: string }> = [
  { w: 320, h: 568, dpr: 2, label: 'iPhone SE (1.ª gen) / 5s' },
  { w: 375, h: 667, dpr: 2, label: 'iPhone SE (2.ª/3.ª gen) / 8' },
  { w: 414, h: 736, dpr: 3, label: 'iPhone 8 Plus' },
  { w: 375, h: 812, dpr: 3, label: 'iPhone X / XS / 11 Pro' },
  { w: 360, h: 780, dpr: 3, label: 'iPhone 12 mini / 13 mini' },
  { w: 414, h: 896, dpr: 2, label: 'iPhone XR / 11' },
  { w: 414, h: 896, dpr: 3, label: 'iPhone XS Max / 11 Pro Max' },
  { w: 390, h: 844, dpr: 3, label: 'iPhone 12 / 13 / 14' },
  { w: 428, h: 926, dpr: 3, label: 'iPhone 12 Pro Max / 13 Pro Max / 14 Plus' },
  { w: 393, h: 852, dpr: 3, label: 'iPhone 15 Pro / 14 Pro / 15 / 16' },
  { w: 430, h: 932, dpr: 3, label: 'iPhone 15 Pro Max / 14 Pro Max / 15 Plus / 16 Plus' },
  { w: 402, h: 874, dpr: 3, label: 'iPhone 16 Pro / 17' },
  { w: 440, h: 956, dpr: 3, label: 'iPhone 16 Pro Max / 17 Pro Max' },
  { w: 420, h: 912, dpr: 3, label: 'iPhone Air' },
  { w: 768, h: 1024, dpr: 2, label: 'iPad / iPad mini' },
  { w: 744, h: 1133, dpr: 2, label: 'iPad mini (6.ª gen)' },
  { w: 810, h: 1080, dpr: 2, label: 'iPad (9.ª gen)' },
  { w: 820, h: 1180, dpr: 2, label: 'iPad Air / iPad (10.ª gen)' },
  { w: 834, h: 1112, dpr: 2, label: 'iPad Pro 10.5 / iPad Air (3.ª gen)' },
  { w: 834, h: 1194, dpr: 2, label: 'iPad Pro 11' },
  { w: 834, h: 1210, dpr: 2, label: 'iPad Pro 11 (M4)' },
  { w: 1024, h: 1366, dpr: 2, label: 'iPad Pro 12.9' },
  { w: 1032, h: 1376, dpr: 2, label: 'iPad Pro 13 (M4)' },
];

/** Modelo aproximado de un iPhone/iPad por su pantalla; si no matchea, el genérico ("iPhone" / "iPad"). */
export function approximateAppleModel(screen: { w: number; h: number; dpr: number }, formFactor: 'phone' | 'tablet'): string {
  const w = Math.min(screen.w, screen.h);
  const h = Math.max(screen.w, screen.h);
  const wantsIpad = formFactor === 'tablet';
  const match = APPLE_MODELS.find(
    (m) => m.w === w && m.h === h && m.dpr === screen.dpr && m.label.startsWith('iPad') === wantsIpad
  );
  return match?.label ?? (wantsIpad ? 'iPad' : 'iPhone');
}

/** Lo que devuelve `navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion'])` (solo Chromium). */
export interface HighEntropyValues {
  model?: string;
  platformVersion?: string;
}

/** Limpia el modelo para guardarlo: solo caracteres comunes y hasta 60; `undefined` si queda vacío (Chrome de escritorio manda ""). */
export function cleanModel(raw: string | undefined): string | undefined {
  const model = (raw ?? '').replace(/[^\p{L}\p{N} \-_./()+,]/gu, '').trim().slice(0, 60);
  return model || undefined;
}
